import React, {useCallback, useState} from "react";
import "./index.scss";
import {connect} from "react-redux";
import {createPortal} from "react-dom";
import {func} from "prop-types";
import {useMediaQuery, useOnClickOutside} from "services/utils/utils";
import {Icon} from "features/components/icon";
import {TextField} from "features/components/text-field";
import {__} from "services/i18n";
import {searchAllStart} from "features/modals/search/reducer";
import {
    searchAllDataSelector,
    searchAllErrorSelector,
    searchAllLoadingSelector
} from "features/modals/search/selectors";
import Text from "features/components/text";
import Title from "features/components/title";
import {historyObject} from "historyObject";
import {MOBILE_MEDIA_QUERY_THRESHOLD} from "constants.js";

/* istanbul ignore next */
const mapStateToProps = state => ({
    loading: searchAllLoadingSelector(state),
    error: searchAllErrorSelector(state),
    data: searchAllDataSelector(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    searchAllStart
};

export const SearchModal = (props) => {
    const {onClose, searchAllStart} = props;
    const {loading, error, data} = props;

    const outsideRef = React.useRef(null);
    /* istanbul ignore next */
    useOnClickOutside(outsideRef, () => {
         onClose();
    });

    const fieldRef = React.useRef(null);

    React.useEffect(() => {
        fieldRef.current.focus();
    }, [fieldRef]);

    const [search, setSearch] = useState("");

    React.useEffect(() => {
        searchAllStart({search});
    }, [search, searchAllStart]);

    const navigateTo = useCallback((link) => {
        onClose();
        historyObject.push(link);
    }, [onClose]);

    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_THRESHOLD);

    const renderContent = useCallback(() => {
        if(loading) return <Text size={16}>{__("common.searching")}</Text>;
        if(error.inError) return <Text size={16} color={"danger"}>{__(error.error)}</Text>;
        if(data.count === 0) return <Text size={16}>{__("modal.search.no-results")}</Text>;
        return <div className="results">
            {!!data.video.length && <div className="result-block">
                <Title size={18}>{__("modal.search.video")}</Title>
                <div className={"result-items"}>
                    {data.video.map(i => <div key={i.id} onClick={() => navigateTo(`/video/${i.id}`)}>
                        <Icon>comment-video</Icon>
                        <Text size={16}>{i.title}</Text>
                    </div>)}
                </div>
            </div>}
            {/*!!data.directory.length && <div className="result-block">
                <Title size={18}>{__("modal.search.directory")}</Title>
                <div className={"result-items"}>
                    {data.directory.map(i => <div key={i.id} onClick={() => navigateTo(`/directory/${i.id}`)}>
                        <Icon>comment-video</Icon>
                        <Text size={16}>{i.title}</Text>
                    </div>)}
                </div>
            </div>*/}
            {!!data.event.length && <div className="result-block">
                <Title size={18}>{__("modal.search.event")}</Title>
                <div className={"result-items"}>
                    {data.event.map(i => <div key={i.id} onClick={() => navigateTo(`/event/${i.id}`)}>
                        <Icon>calendar</Icon>
                        <Text size={16}>{i.title}</Text>
                    </div>)}
                </div>
            </div>}
            {!!data.threads.length && <div className="result-block">
                <Title size={18}>{__("modal.search.threads")}</Title>
                <div className={"result-items"}>
                    {data.threads.map(i => <div key={i.id} onClick={() => navigateTo(`/threads/${i.id}`)}>
                        <Icon>calendar</Icon>
                        <Text size={16}>{i.title}</Text>
                    </div>)}
                </div>
            </div>}
            {(!!data.users && !!data.users.length) && <div className="result-block">
                <Title size={18}>{__("modal.search.users")}</Title>
                <div className={"result-items"}>
                    {data.users.map(i => <div key={i.id} onClick={() => navigateTo(`/people/${i.slug}`)}>
                        <Icon>account-circle</Icon>
                        <Text size={16}>{i.title}</Text>
                    </div>)}
                </div>
            </div>}
            {(!!data.companies && !!data.companies.length) && <div className="result-block">
                <Title size={18}>{__("modal.search.companies")}</Title>
                <div className={"result-items"}>
                    {data.companies.map(i => <div key={i.id} onClick={() => navigateTo(`/people/${i.slug}/company/${i.id}`)}>
                        <Icon>poll</Icon>
                        <Text size={16}>{i.title}</Text>
                    </div>)}
                </div>
            </div>}
        </div>
    }, [data, error, loading, navigateTo]);

    return <div ref={outsideRef} className={"search-modal"}>
        <div className={"row"}>
            <Icon>search</Icon>
            <TextField refValue={fieldRef}
                       type={"text"}
                       value={search}
                       onChange={setSearch}
                       placeholder={__(!isMobile ? "modal.search.placeholder" : "modal.search.short-placeholder")}
            />
            <div onClick={() => onClose()} className="clear-btn"><Icon>clear-circle-outlined</Icon></div>
        </div>
        {renderContent()}
    </div>
};
SearchModal.propTypes = {
    onClose: func.isRequired
}

const SearchModalConnected = connect(mapStateToProps, mapDispatchToProps)(SearchModal);
/* istanbul ignore next */
export const SearchModalPortal = props => createPortal(
    <div className={"portal search-modal-portal"}><SearchModalConnected {...props}/></div>,
    document.body
);